import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useSubscription } from '@/hooks/useSubscription';
import dayjs from 'dayjs';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export const DangerZone = () => {
  const { subscription, features, cancelSubscription, reactivateSubscription, loading } = useSubscription();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  const canCancel = (features.isActive || features.isTrialing) && !features.isCancelScheduled;
  const canReactivate = features.isCancelScheduled;

  if (!canCancel && !canReactivate) {
    return null;
  }

  console.log(subscription);
  return (
    <>
      <Card className='border-red-200 dark:border-red-800'>
        <CardHeader>
          <CardTitle className='flex items-center gap-2 text-red-600 dark:text-red-400'>
            <AlertTriangle className='size-5' />
            Danger Zone
          </CardTitle>
          <CardDescription>Irreversible actions that affect your entire organisation.</CardDescription>
        </CardHeader>
        <CardContent>
          {canCancel && (
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-slate-900 dark:text-white'>Cancel subscription</p>
                <p className='text-sm text-slate-500 dark:text-stone-400'>
                  All uploaded and processed data will be permanently deleted.
                </p>
              </div>
              <Button variant='destructive' onClick={() => setCancelDialogOpen(true)}>
                Cancel Subscription
              </Button>
            </div>
          )}

          {canReactivate && (
            <div className='space-y-3'>
              <div>
                <p className='text-sm font-medium text-slate-900 dark:text-white'>Cancellation scheduled</p>
                <p className='text-sm text-slate-500 dark:text-stone-400'>
                  Your subscription will be cancelled on{' '}
                  {subscription.currentPeriodEnd
                    ? dayjs(subscription.currentPeriodEnd).format('MMM D, YYYY')
                    : 'the end of the billing period'}
                  . Reactivate to keep your data.
                </p>
              </div>
              <Button
                variant='outline'
                disabled={reactivating || loading}
                onClick={async () => {
                  setReactivating(true);
                  try {
                    await reactivateSubscription();
                    toast.success('Subscription reactivated successfully.');
                  } catch {
                    toast.error('Failed to reactivate subscription. Please try again.');
                  } finally {
                    setReactivating(false);
                  }
                }}>
                {reactivating ? 'Reactivating…' : 'Reactivate'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel subscription</DialogTitle>
            <DialogDescription>
              {subscription.currentPeriodEnd ? (
                <>
                  Your subscription will remain active until{' '}
                  <span className='font-medium'>{dayjs(subscription.currentPeriodEnd).format('MMM D, YYYY')}</span>.
                  After that, all uploaded and processed data for your organisation will be permanently deleted. This
                  cannot be undone.
                </>
              ) : (
                <>
                  Your subscription will be cancelled at the end of the current billing period. After that, all uploaded
                  and processed data for your organisation will be permanently deleted. This cannot be undone.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>
              Keep Subscription
            </Button>
            <Button
              variant='destructive'
              disabled={cancelling}
              onClick={async () => {
                setCancelling(true);
                try {
                  await cancelSubscription();
                  setCancelDialogOpen(false);
                } catch {
                  // error is surfaced via the hook's error state
                } finally {
                  setCancelling(false);
                }
              }}>
              {cancelling ? 'Cancelling…' : 'Yes, Cancel Subscription'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
